<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import imgServiceCycleCalendar from '@/assets/编组 2@2x.png'
import imgAlertRed from '@/assets/alert.red@2x.png'
import imgClose from '@/assets/Fill 1@2x.png'
import CalendarMini from './CalendarMini.vue'
import ModalButtonSpinner from '@/components/ModalButtonSpinner.vue'
import type { ServiceName } from '@/api/hmCareOrderTypes'
import type { CareOrderMetaData, CareTemplateMeta } from '@/api/orders'
import { SERVICE_TIME_TEMPLATES, addDaysInclusive, formatDeadline } from '@/api/hmCareOrderTypes'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import { validateFieldLimits } from '@/utils/fieldLimitValidation'

const orderFieldLimits = FIELD_LIMITS.order

export interface CreateOrderPayload {
  orderId: string
  homeId: string
  /** 套餐展示名（与 meta.plans[].label 或本地选项一致） */
  service: string
  serviceCycleDate: string
  serviceAddress: string
  contactName: string
  phone: string
  /** 有 meta 且提交远程建单时填写，对应 `plan_key` */
  planKey?: string
  /** 有 meta 且提交远程建单时填写，对应 `care_template_id` */
  careTemplateId?: number
  /** 对应接口 `notes`，可选 */
  notes?: string
}

const props = withDefaults(
  defineProps<{
    open: boolean
    existingOrderIds: string[]
    /** 来自 POST /api/admin/care-order/meta；为空时用本地套餐与时间模板 */
    careMeta: CareOrderMetaData | null
    /** 为 true 时整页嵌入（无遮罩、不 Teleport 到 body），用于独立路由「新建订单」页 */
    embedded?: boolean
    /** 父页注入 Toast（比 inject 更可靠，Teleport 子树也能用） */
    onToast?: (type: 'success' | 'warning' | 'error', message: string) => void
    /** 提交前刷新已存在的 Order ID（避免列表尚未拉完） */
    beforeDuplicateCheck?: () => void | Promise<void>
    /** 按 Order ID 向接口复核是否已存在 */
    checkOrderIdExists?: (orderId: string) => Promise<boolean>
    /** 父组件异步提交中，锁定 Submit */
    submitting?: boolean
  }>(),
  { embedded: false, submitting: false },
)

const emit = defineEmits<{
  (e: 'update:open', v: boolean): void
  (e: 'created', payload: CreateOrderPayload): void
  (e: 'cancel'): void
}>()

const showToastInject = inject<(t: 'success' | 'warning' | 'error', m: string) => void>('showToast')

function fireToast(type: 'success' | 'warning' | 'error', message: string) {
  if (props.onToast) {
    props.onToast(type, message)
    return
  }
  showToastInject?.(type, message)
}

const isPanelVisible = computed(() => props.embedded || props.open)

const form = reactive({
  orderId: '',
  homeId: '',
  service: '' as string,
  serviceCycleDate: '',
  serviceAddress: '',
  contactName: '',
  phone: '',
  careTemplateId: null as number | null,
})

const touched = reactive({
  orderId: false,
  homeId: false,
  service: false,
  serviceCycleDate: false,
  serviceAddress: false,
  contactName: false,
  phone: false,
})

const showErrors = reactive({ submitAttempt: false })
/** 接口查重结果（按需 search，不预拉全量 list） */
const orderIdRemoteDuplicate = ref(false)
let orderIdRemoteCheckSeq = 0

const serviceCycleCalRef = ref<HTMLElement | null>(null)
const serviceCycleCalOpen = ref(false)
const serviceCalMonth = ref(new Date().getMonth())
const serviceCalYear = ref(new Date().getFullYear())
const serviceSelectRef = ref<HTMLElement | null>(null)
const serviceDropdownOpen = ref(false)
/** 选中选项后短暂忽略触发器 click，避免下拉再次打开 */
const serviceSelectLock = ref(false)

function closeServiceDropdown(markTouched = true): void {
  serviceDropdownOpen.value = false
  if (markTouched) touched.service = true
}

function toggleServiceDropdown(): void {
  if (serviceSelectLock.value) return
  if (serviceDropdownOpen.value) {
    closeServiceDropdown(true)
  } else {
    serviceDropdownOpen.value = true
  }
}

function selectService(value: string): void {
  form.service = value
  touched.service = true
  serviceDropdownOpen.value = false
  serviceSelectLock.value = true
  window.setTimeout(() => {
    serviceSelectLock.value = false
  }, 0)
}

function syncServiceCycleCalMonthYear(): void {
  const ymd = form.serviceCycleDate.trim()
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (ymdMatch) {
    serviceCalYear.value = Number(ymdMatch[1])
    serviceCalMonth.value = Number(ymdMatch[2]) - 1
    return
  }
  const now = new Date()
  serviceCalMonth.value = now.getMonth()
  serviceCalYear.value = now.getFullYear()
}

function toggleServiceCycleCal(): void {
  if (serviceCycleCalOpen.value) {
    serviceCycleCalOpen.value = false
    return
  }
  syncServiceCycleCalMonthYear()
  serviceCycleCalOpen.value = true
  serviceDropdownOpen.value = false
}

function onPickServiceCycleDate(ymd: string): void {
  form.serviceCycleDate = ymd
  touched.serviceCycleDate = true
  serviceCycleCalOpen.value = false
}

function onDocumentPointerDown(e: PointerEvent): void {
  const target = e.target as Node
  if (serviceDropdownOpen.value) {
    const root = serviceSelectRef.value
    if (root && !root.contains(target)) {
      closeServiceDropdown(true)
    }
  }
  if (serviceCycleCalOpen.value) {
    const root = serviceCycleCalRef.value
    if (root && !root.contains(target)) {
      serviceCycleCalOpen.value = false
    }
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown)
})

const LOCAL_PLAN_LABELS = ['Essential Care', 'Enhanced Care', 'Premium Care'] as const

function templatePickValue(t: CareTemplateMeta): number {
  return typeof t.value === 'number' ? t.value : t.id
}

watch(isPanelVisible, (visible) => {
  if (!visible) serviceCycleCalOpen.value = false
})

watch(
  () => form.service,
  (svc) => {
    if (!svc || !props.careMeta?.templates_by_plan) {
      form.careTemplateId = null
      return
    }
    const plan = props.careMeta.plans.find((p) => p.label === svc)
    if (!plan) {
      form.careTemplateId = null
      return
    }
    const templates = props.careMeta.templates_by_plan[plan.value] ?? []
    const firstTpl = templates[0]
    form.careTemplateId = firstTpl != null ? templatePickValue(firstTpl) : null
  },
)

const serviceOptions = computed(() => {
  const plans = props.careMeta?.plans
  if (plans?.length) return plans.map((p) => p.label)
  return [...LOCAL_PLAN_LABELS]
})

const timeTemplateLines = computed(() => {
  if (!form.service) return []
  const meta = props.careMeta
  if (meta?.plans?.length && meta.templates_by_plan) {
    const plan = meta.plans.find((p) => p.label === form.service)
    if (!plan) return []
    const arr = meta.templates_by_plan[plan.value] ?? []
    return arr.map((t) => t.label)
  }
  return SERVICE_TIME_TEMPLATES[form.service as ServiceName] ?? []
})

const selectedPlanValue = computed(() => {
  if (!form.service || !props.careMeta?.plans) return ''
  return props.careMeta.plans.find((p) => p.label === form.service)?.value ?? ''
})

const deadlineHint = computed(() => {
  if (!form.serviceCycleDate) return ''
  const pv = selectedPlanValue.value
  if (pv !== 'essential_care' && pv !== 'enhanced_care') {
    if (form.service !== 'Essential Care' && form.service !== 'Enhanced Care') return ''
  }
  const d = new Date(form.serviceCycleDate + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return ''
  const end = addDaysInclusive(d, 90)
  return `Service deadline (auto): ${formatDeadline(end)} (90 days from start date, inclusive)`
})

function resetForm() {
  form.orderId = ''
  form.homeId = ''
  form.service = ''
  form.serviceCycleDate = ''
  form.serviceAddress = ''
  form.contactName = ''
  form.phone = ''
  form.careTemplateId = null
  Object.keys(touched).forEach((k) => {
    ;(touched as Record<string, boolean>)[k] = false
  })
  showErrors.submitAttempt = false
  orderIdRemoteDuplicate.value = false
  serviceDropdownOpen.value = false
}

watch(
  () => isPanelVisible.value,
  (v) => {
    if (v) resetForm()
  },
)

function close() {
  if (props.submitting) return
  if (props.embedded) emit('cancel')
  else emit('update:open', false)
}

const fieldInvalid = computed(() => {
  return {
    orderId: !isDigitsOnlyId(form.orderId),
    homeId: !isDigitsOnlyId(form.homeId),
    service: !form.service,
    serviceCycleDate: !form.serviceCycleDate,
    serviceAddress: !form.serviceAddress.trim(),
    contactName: !form.contactName.trim(),
    phone: !form.phone.trim(),
  }
})

function shouldShowFieldError(key: keyof typeof fieldInvalid.value) {
  return (touched[key] || showErrors.submitAttempt) && fieldInvalid.value[key]
}

function isDigitsOnlyId(value: string): boolean {
  const v = value.trim()
  return v.length > 0 && /^\d+$/.test(v)
}

let lastDigitsOnlyIdToastAt = 0

function showDigitsOnlyIdToast(label: 'Order ID' | 'Home ID') {
  const now = Date.now()
  if (now - lastDigitsOnlyIdToastAt < 1200) return
  lastDigitsOnlyIdToastAt = now
  fireToast('warning', `${label} must contain numbers only.`)
}

function onDigitsOnlyIdInput(field: 'orderId' | 'homeId', e: Event) {
  const el = e.target as HTMLInputElement
  const limit = orderFieldLimits[field]
  const raw = el.value
  const next = raw.replace(/\D/g, '').slice(0, limit)
  if (raw !== next) {
    showDigitsOnlyIdToast(field === 'orderId' ? 'Order ID' : 'Home ID')
  }
  form[field] = next
  if (field === 'orderId') orderIdRemoteDuplicate.value = false
  if (el.value !== next) el.value = next
}

function normalizeExistingIdKey(v: string): string {
  return String(v).trim().toLowerCase()
}

function isOrderIdDuplicate(): boolean {
  const oid = form.orderId.trim()
  if (!oid) return false
  const key = normalizeExistingIdKey(oid)
  if (props.existingOrderIds.some((x) => normalizeExistingIdKey(x) === key)) return true
  return orderIdRemoteDuplicate.value
}

function shouldShowOrderIdDuplicateError(): boolean {
  return (touched.orderId || showErrors.submitAttempt) && isOrderIdDuplicate()
}

async function onOrderIdBlur(): Promise<void> {
  touched.orderId = true
  const oid = form.orderId.trim()
  if (!oid || !props.checkOrderIdExists) {
    orderIdRemoteDuplicate.value = false
    return
  }
  const seq = ++orderIdRemoteCheckSeq
  const exists = await props.checkOrderIdExists(oid)
  if (seq !== orderIdRemoteCheckSeq) return
  orderIdRemoteDuplicate.value = exists === true
}

function onSubmit() {
  void submitOrder()
}

function validateCreateOrderFieldLimits(): string | null {
  return validateFieldLimits([
    { value: form.orderId.trim(), max: orderFieldLimits.orderId, label: 'Order ID' },
    { value: form.homeId.trim(), max: orderFieldLimits.homeId, label: 'Home ID' },
    { value: form.serviceAddress.trim(), max: orderFieldLimits.serviceAddress, label: 'Service Address' },
    { value: form.contactName.trim(), max: orderFieldLimits.contactName, label: 'Contact name' },
    { value: form.phone.trim(), max: orderFieldLimits.contactPhone, label: 'Phone Number' },
  ])
}

async function submitOrder() {
  showErrors.submitAttempt = true

  const limitErr = validateCreateOrderFieldLimits()
  if (limitErr) {
    fireToast('warning', limitErr)
    return
  }

  try {
    await props.beforeDuplicateCheck?.()
  } catch {
    /* 刷新失败仍用当前缓存做重复校验 */
  }

  const oid = form.orderId.trim()
  const hid = form.homeId.trim()

  // 重复 Order ID 优先于「必填未填」
  if (oid) {
    const orderIdTaken =
      isOrderIdDuplicate() || (await props.checkOrderIdExists?.(oid)) === true
    if (orderIdTaken) {
      fireToast('warning', 'Order ID already exists')
      return
    }
  }

  const inv = fieldInvalid.value
  if (
    inv.orderId ||
    inv.homeId ||
    inv.service ||
    inv.serviceCycleDate ||
    inv.serviceAddress ||
    inv.contactName ||
    inv.phone
  ) {
    fireToast('warning', 'Please complete all required fields.')
    return
  }

  if (props.careMeta?.plans?.length) {
    const plan = props.careMeta.plans.find((p) => p.label === form.service)
    const tpls = plan ? (props.careMeta.templates_by_plan?.[plan.value] ?? []) : []
    if (plan && !tpls.length) {
      fireToast('warning', 'No care template for this plan. Cannot submit.')
      return
    }
    if (plan && tpls.length && form.careTemplateId == null) {
      fireToast('warning', 'No care template for this plan. Cannot submit.')
      return
    }
  }
  const plan = props.careMeta?.plans.find((p) => p.label === form.service)
  emit('created', {
    orderId: oid,
    homeId: hid,
    service: form.service,
    serviceCycleDate: form.serviceCycleDate,
    serviceAddress: form.serviceAddress.trim(),
    contactName: form.contactName.trim(),
    phone: form.phone.trim(),
    planKey: plan?.value,
    careTemplateId: form.careTemplateId ?? undefined,
  })
}
</script>

<template>
  <Teleport to="body" :disabled="embedded">
    <div
      v-if="isPanelVisible"
      :class="embedded ? 'hm-create-embedded-root' : 'hm-modal-root'"
    >
      <div
        class="hm-modal"
        :class="{ 'hm-modal--embedded': embedded }"
        role="dialog"
        :aria-modal="true"
        aria-labelledby="hm-create-title"
      >
        <header class="hm-modal-head">
          <h2 id="hm-create-title">Create New Order</h2>
          <button type="button" class="hm-modal-x" aria-label="Close" :disabled="submitting" @click="close">
            <img :src="imgClose" class="hm-modal-x-ic" alt="" />
          </button>
        </header>

        <div class="hm-modal-body">
          <label class="hm-field">
            <span class="hm-label"><span class="req">*</span> Order ID</span>
            <div
              class="hm-input-wrap"
              :class="{ err: shouldShowFieldError('orderId') || shouldShowOrderIdDuplicateError() }"
            >
              <input
                v-model="form.orderId"
                type="text"
                inputmode="numeric"
                class="hm-input"
                placeholder="Enter Order ID"
                :maxlength="orderFieldLimits.orderId"
                @input="onDigitsOnlyIdInput('orderId', $event)"
                @blur="onOrderIdBlur"
              />
              <img
                v-if="shouldShowFieldError('orderId') || shouldShowOrderIdDuplicateError()"
                :src="imgAlertRed"
                class="hm-field-ico"
                alt=""
                aria-hidden="true"
              />
            </div>
            <p v-if="shouldShowOrderIdDuplicateError()" class="hm-field-hint hm-field-hint--err">
              Order ID already exists
            </p>
          </label>

          <label class="hm-field">
            <span class="hm-label"><span class="req">*</span> Home ID</span>
            <div
              class="hm-input-wrap"
              :class="{ err: shouldShowFieldError('homeId') }"
            >
              <input
                v-model="form.homeId"
                type="text"
                inputmode="numeric"
                class="hm-input"
                placeholder="Enter Home ID"
                :maxlength="orderFieldLimits.homeId"
                @input="onDigitsOnlyIdInput('homeId', $event)"
                @blur="touched.homeId = true"
              />
              <img
                v-if="shouldShowFieldError('homeId')"
                :src="imgAlertRed"
                class="hm-field-ico"
                alt=""
                aria-hidden="true"
              />
            </div>
          </label>

          <label class="hm-field">
            <span class="hm-label"><span class="req">*</span> Service</span>
            <div
              ref="serviceSelectRef"
              class="hm-input-wrap hm-select-wrap hm-select-wrap--trail hm-service-select"
              :class="{
                err: shouldShowFieldError('service'),
                'hm-service-select--open': serviceDropdownOpen,
              }"
              role="combobox"
              :aria-expanded="serviceDropdownOpen"
              aria-haspopup="listbox"
            >
              <button
                type="button"
                class="hm-service-select-trigger"
                @click="toggleServiceDropdown"
              >
                <span
                  class="hm-service-select-value"
                  :class="{ 'hm-service-select-value--placeholder': !form.service }"
                >
                  {{ form.service || 'Select Service' }}
                </span>
              </button>
              <div class="hm-select-trail">
                <img
                  v-if="shouldShowFieldError('service')"
                  :src="imgAlertRed"
                  class="hm-field-ico"
                  alt=""
                />
                <button
                  type="button"
                  class="hm-chev-btn"
                  aria-label="Open service options"
                  @click="toggleServiceDropdown"
                >
                  <span class="hm-chev">
                    <img src="@/assets/downarrow@2x.png" alt="" />
                  </span>
                </button>
              </div>
              <ul
                v-show="serviceDropdownOpen"
                class="hm-service-dropdown"
                role="listbox"
                @click.stop
              >
                <li
                  v-for="s in serviceOptions"
                  :key="s"
                  role="option"
                  :aria-selected="form.service === s"
                  class="hm-service-dropdown-item"
                  :class="{ 'hm-service-dropdown-item--selected': form.service === s }"
                  @mousedown.prevent.stop="selectService(s)"
                >
                  {{ s }}
                </li>
              </ul>
            </div>
          </label>

          <label class="hm-field">
            <span class="hm-label hm-label--service-sycle"
              ><span class="req">*</span> Service Cycle</span
            >
            <div
              ref="serviceCycleCalRef"
              class="hm-input-wrap hm-date-wrap hm-date-wrap--service-sycle"
              :class="{ err: shouldShowFieldError('serviceCycleDate') }"
            >
              <div class="hm-date-input-slot" @click="toggleServiceCycleCal">
                <input
                  :value="form.serviceCycleDate"
                  type="text"
                  readonly
                  class="hm-input hm-date hm-date--service-sycle"
                  :class="{ 'hm-date--service-sycle-empty': !form.serviceCycleDate }"
                  placeholder=""
                  @blur="touched.serviceCycleDate = true"
                />
                <span v-if="!form.serviceCycleDate" class="hm-date-placeholder"
                  >Select a starting date</span
                >
              </div>
              <div class="hm-date-trail" aria-hidden="true">
                <img
                  v-if="shouldShowFieldError('serviceCycleDate')"
                  :src="imgAlertRed"
                  class="hm-field-ico"
                  alt=""
                />
                <span class="hm-date-trail-divider" />
                <button
                  type="button"
                  class="hm-date-cal-btn"
                  aria-label="Select service cycle date"
                  @click.stop="toggleServiceCycleCal"
                >
                  <img :src="imgServiceCycleCalendar" class="hm-date-cal-ic" alt="" />
                </button>
              </div>
              <div v-if="serviceCycleCalOpen" class="hm-calendar-pop" @click.stop @mousedown.stop>
                <CalendarMini
                  v-model:month="serviceCalMonth"
                  v-model:year="serviceCalYear"
                  compact
                  :selected-ymd="form.serviceCycleDate"
                  @pick="onPickServiceCycleDate"
                />
              </div>
            </div>
            <p v-if="deadlineHint" class="hm-deadline">{{ deadlineHint }}</p>
          </label>

          <div v-if="timeTemplateLines.length" class="hm-template">
            <div class="hm-template-title">Time template (for selected service)</div>
            <ul>
              <li v-for="(line, i) in timeTemplateLines" :key="i">{{ line }}</li>
            </ul>
          </div>

          <label class="hm-field">
            <span class="hm-label"><span class="req">*</span> Service Address</span>
            <div class="hm-input-wrap" :class="{ err: shouldShowFieldError('serviceAddress') }">
              <input
                v-model="form.serviceAddress"
                type="text"
                class="hm-input"
                placeholder="Enter service address"
                :maxlength="orderFieldLimits.serviceAddress"
                @blur="touched.serviceAddress = true"
              />
              <img
                v-if="shouldShowFieldError('serviceAddress')"
                :src="imgAlertRed"
                class="hm-field-ico"
                alt=""
                aria-hidden="true"
              />
            </div>
          </label>

          <label class="hm-field">
            <span class="hm-label"><span class="req">*</span> Contact name</span>
            <div class="hm-input-wrap" :class="{ err: shouldShowFieldError('contactName') }">
              <input
                v-model="form.contactName"
                type="text"
                class="hm-input"
                placeholder="Enter contact name"
                :maxlength="orderFieldLimits.contactName"
                @blur="touched.contactName = true"
              />
              <img
                v-if="shouldShowFieldError('contactName')"
                :src="imgAlertRed"
                class="hm-field-ico"
                alt=""
                aria-hidden="true"
              />
            </div>
          </label>

          <label class="hm-field">
            <span class="hm-label"><span class="req">*</span> Phone Number</span>
            <div class="hm-input-wrap" :class="{ err: shouldShowFieldError('phone') }">
              <input
                v-model="form.phone"
                type="text"
                class="hm-input"
                placeholder="Enter Phone Number 123-456-7890"
                :maxlength="orderFieldLimits.contactPhone"
                @blur="touched.phone = true"
              />
              <img
                v-if="shouldShowFieldError('phone')"
                :src="imgAlertRed"
                class="hm-field-ico"
                alt=""
                aria-hidden="true"
              />
            </div>
          </label>
        </div>

        <footer class="hm-modal-foot">
          <button type="button" class="hm-btn-outline" :disabled="submitting" @click="close">
            <span class="hm-btn-outline-txt">Cancel</span>
          </button>
          <button type="button" class="hm-btn hm-btn-solid" :disabled="submitting" @click="onSubmit">
            <span class="hm-modal-btn-content" :class="{ 'hm-modal-btn-content--loading': submitting }">
              <span class="hm-btn-solid-txt">Submit</span>
              <ModalButtonSpinner v-if="submitting" />
            </span>
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.hm-modal-root {
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.hm-create-embedded-root {
  box-sizing: border-box;
  min-height: auto;
  padding: 0;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
}

.hm-modal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  --hm-modal-pad: 24px;
  padding: var(--hm-modal-pad);
  width: min(530px, 100%);
  height: 716px;
  max-height: min(716px, calc(100vh - 48px));
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  box-shadow: none;
}

.hm-modal--embedded {
  width: 100%;
  max-width: min(560px, 100%);
  height: auto;
  max-height: none;
  border: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: var(--hm-modal-pad);
}

.hm-modal--embedded .hm-modal-body {
  overflow: visible;
  flex: none;
  min-height: auto;
}

.hm-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-bottom: 24px;
}

/* 页标题 Create New Order：SF Pro Bold 16/24 #000 左对齐 */
.hm-modal-head h2,
#hm-create-title {
  margin: 0;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 16px;
  font-weight: 700;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
}

.hm-modal-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  margin: 0;
  border-radius: 8px;
}

.hm-modal-x-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.hm-modal-back {
  border: none;
  background: transparent;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  color: #111;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 8px;
}

.hm-modal-back:hover {
  background: #f0f0f0;
}

.hm-modal-body {
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

/* 滚动条贴弹窗最右侧；内容与弹窗右缘间距 24px（含滚动条宽度） */
.hm-modal:not(.hm-modal--embedded) .hm-modal-body,
.hm-modal:not(.hm-modal--embedded) .hm-modal-foot {
  box-sizing: border-box;
  width: calc(100% + var(--hm-modal-pad));
  margin-right: calc(-1 * var(--hm-modal-pad));
  padding-right: var(--hm-modal-pad);
}

.hm-modal:not(.hm-modal--embedded) .hm-modal-body {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.hm-modal:not(.hm-modal--embedded) .hm-modal-body::-webkit-scrollbar {
  width: 6px;
}

.hm-modal:not(.hm-modal--embedded) .hm-modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.hm-modal:not(.hm-modal--embedded) .hm-modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.hm-modal:not(.hm-modal--embedded) .hm-modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* 输入框底 → 下一标签顶：24px；最后一项（Phone）→ 按钮组：32px */
.hm-field {
  display: block;
  margin-bottom: 24px;
}

.hm-modal-body > .hm-field:last-child {
  margin-bottom: 32px;
}

/* 小标题（* + 文案）：SF Pro Regular 14/20，左对齐、顶对齐 */
.hm-label {
  display: block;
  margin: 0 0 8px;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
}

/* 必填 *：同色阶 14/20，#E54848；* 与标签文字间距 4px */
.req {
  color: #e54848;
  font-weight: 400;
  margin-right: 4px;
}

/* Service Sycle 小标题：PingFang SC Regular 14/20 #000；标签→输入框 8px */
.hm-label--service-sycle {
  margin-bottom: 8px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    -apple-system,
    BlinkMacSystemFont,
    'Helvetica Neue',
    'Microsoft YaHei',
    sans-serif;
}

/* 各输入框：482×32、白底、圆角 16、1px 描边 */
.hm-input-wrap {
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 0 16px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #d8d8d8;
  transition: border-color 0.15s ease;
}

.hm-input-wrap:focus-within:not(.err),
.hm-input-wrap.hm-service-select--open:not(.err) {
  border-color: #666666;
}

.hm-input-wrap:has(.hm-field-ico),
.hm-input-wrap:has(.hm-date-trail),
.hm-input-wrap:has(.hm-select-trail) {
  padding-right: 12px;
}

.hm-input-wrap.err {
  border-color: #e53935;
}

.hm-field-hint--err {
  margin: 4px 0 0;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #e54848;
}

.hm-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 20px;
  color: #111;
  min-width: 0;
  background: transparent;
  padding: 0;
}

/* 占位文案（除 Service Sycle 日期外）：SF Pro Regular 14/20 #CCC */
.hm-input:not(.hm-date--service-sycle)::placeholder {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #cccccc;
  opacity: 1;
}

.hm-input:not(.hm-date--service-sycle)::-webkit-input-placeholder {
  color: #cccccc;
}

.hm-input:not(.hm-date--service-sycle)::-moz-placeholder {
  color: #cccccc;
  opacity: 1;
}

.hm-date-wrap {
  gap: 8px;
}

/* Service Sycle：感叹号 | 竖线 | 日历 */
.hm-date-wrap--service-sycle {
  position: relative;
  gap: 8px;
}

.hm-date-input-slot {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.hm-date-placeholder {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #cccccc;
  white-space: nowrap;
}

.hm-date-trail {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 3px;
  margin-left: 0;
}

.hm-date-trail .hm-field-ico {
  margin-left: 0;
}

/* 竖线分隔：1×16 #D8D8D8 */
.hm-date-trail-divider {
  width: 1px;
  height: 16px;
  flex-shrink: 0;
  background: #d8d8d8;
}

.hm-date {
  font-family: inherit;
}

/* Service Sycle 占用态：SF Pro Regular 14/20 #999；已选日期为 #000 */
.hm-date--service-sycle {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
}

.hm-date--service-sycle.hm-date--service-sycle-empty {
  color: transparent;
}

.hm-calendar-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 13002;
}

.hm-calendar-pop :deep(.cal-mini--compact) {
  --cal-cell-size: 28px;
  --cal-cell-gap: 5px;
  padding: 14px 16px;
  border-radius: 12px;
}

.hm-calendar-pop :deep(.cal-mini--compact .cal-head) {
  gap: 32px;
  margin-bottom: 12px;
}

.hm-calendar-pop :deep(.cal-mini--compact .cal-head-month),
.hm-calendar-pop :deep(.cal-mini--compact .cal-head-year) {
  gap: 16px;
}

.hm-calendar-pop :deep(.cal-mini--compact .cal-weeks) {
  margin-bottom: 8px;
}

.hm-date-cal-btn {
  border: none;
  background: transparent;
  padding: 0;
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.hm-date-cal-ic {
  width: 22px;
  height: 22px;
  display: block;
  object-fit: contain;
}

/* Service 自定义下拉 */
.hm-service-select {
  position: relative;
  cursor: pointer;
}

.hm-service-select-trigger {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
}

.hm-service-select-value {
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #111;
}

.hm-service-select-value--placeholder {
  color: #cccccc;
}

.hm-chev-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

/* Service 选项浮层：482×128 */
.hm-service-dropdown {
  box-sizing: border-box;
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 13001;
  width: 100%;
  max-width: 100%;
  height: 128px;
  margin: 0;
  padding: 0;
  list-style: none;
  background: #ffffff;
  border: 1px solid #d8d8d8;
  border-radius: 16px;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hm-service-dropdown::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.hm-service-dropdown-item {
  box-sizing: border-box;
  flex: 1 0 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Display',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #666666;
  cursor: pointer;
}

.hm-service-dropdown-item:hover {
  background: #f5f5f5;
}

.hm-service-dropdown-item--selected {
  font-weight: 700;
  color: #000000;
}

/* Service：右侧 16×16 下拉 + 校验图标 */
.hm-select-trail {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 3px;
  margin-left: 0;
}

.hm-select-trail .hm-field-ico {
  margin-left: 0;
}

.hm-select-trail .hm-chev {
  position: static;
  transform: none;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.hm-select-trail .hm-chev img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  opacity: 0.55;
}

.hm-chev {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.hm-chev img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  opacity: 0.55;
}

.hm-field-ico {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  display: block;
  flex-shrink: 0;
  margin: 0;
  object-fit: contain;
}

.hm-template {
  box-sizing: border-box;
  width: 100%;
  margin: -4px 0 14px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  font-size: 0.82rem;
  color: #444;
}

.hm-template-title {
  font-weight: 700;
  margin-bottom: 6px;
  color: #222;
}

.hm-template ul {
  margin: 0;
  padding-left: 18px;
}

.hm-deadline {
  margin-top: 8px;
  font-size: 0.82rem;
  color: #2e7d32;
  font-weight: 600;
}

.hm-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
}

.hm-btn {
  min-width: 100px;
  height: 40px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

/* Cancel：外框 80×32 1px #000 圆角 16；文案 SF Pro Semibold 14/24 #000 右对齐 */
.hm-btn-outline {
  box-sizing: border-box;
  width: 80px;
  height: 32px;
  border-radius: 16px;
  border: 1px solid #000000;
  min-width: 80px;
  padding: 0 16px 0 0;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
}

.hm-btn-outline-txt {
  box-sizing: border-box;
  display: block;
  width: 47px;
  height: 24px;
  font-family: SFPro, SFPro;
  font-weight: 600;
  font-size: 14px;
  color: #000000;
  line-height: 24px;
  text-align: right;
  font-style: normal;
}

.hm-btn-outline:hover:not(:disabled) {
  background: #000000;
}

.hm-btn-outline:hover:not(:disabled) .hm-btn-outline-txt {
  color: #ffffff;
}

/* Submit：外框 80×32 #000 圆角 16；文案 SF Pro Semibold 14/24 #FFF 左对齐 */
.hm-btn-solid {
  box-sizing: border-box;
  width: 80px;
  min-width: 80px;
  height: 32px;
  padding: 0 0 0 16px;
  background: #000000;
  border: none;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
}

.hm-btn-solid-txt {
  box-sizing: border-box;
  display: block;
  width: 48px;
  height: 24px;
  font-family: SFPro, SFPro;
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  line-height: 24px;
  text-align: left;
  font-style: normal;
}
.hm-btn-solid:hover:not(:disabled) {
  background: #484848;
}
</style>
